import {
  BLUE_YONDER_PROVIDER,
  type DropoffProviderType,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "@redotech/redo-return-core-common/dropoff-provider";
import {
  IntegrationProviders,
  TrackstarReturnProvider,
} from "@redotech/redo-return-core-common/return-integration-types";
import {
  getDropoffIdentifierForProvider,
  isDropoffChosen,
} from "../../dropoff/utils";
import type { ReturnZod } from "../../return";

export const BLUEYONDER_INTEGRATION = "BlueYonder";
export const RETURNBEAR_INTEGRATION = "ReturnBear";
export const RETURNLOGIC_INTEGRATION = "ReturnLogic";

export enum ExternalRmaIntegration {
  AIMS360 = "Aims360",
  APPAREL_MAGIC = "ApparelMagic",
  BLUE_CHERRY = "BlueCherry",
  BRIGHTPEARL = "Brightpearl",
  CIN7_OMNI = "Cin7Omni",
  CLOUDX = "CloudX",
  DEPOSCO = "Deposco",
  ECWID = "Ecwid",
  EXTENSIV = "Extensiv",
  FLEXPORT = "Flexport",
  GLOBALE = "GlobalE",
  INMAR = "Inmar",
  MASONHUB = "Masonhub",
  MINTSOFT = "Mintsoft",
  NETSUITE = "NetSuite",
  RYDER = "Ryder",
  SFOM = "SFOM",
  SHIPBOB = "ShipBob",
  SHIPEDGE = "Shipedge",
  SHIPHERO = "ShipHero",
  SHIPMONK = "ShipMonk",
  TRACKSTAR = "Trackstar",
}

export const CreateExternalIntegrationIdProvider = {
  ...ExternalRmaIntegration,
  BLUEYONDER: BLUEYONDER_INTEGRATION,
  RETURNBEAR: RETURNBEAR_INTEGRATION,
  RETURNLOGIC: RETURNLOGIC_INTEGRATION,
} as const;
export type CreateExternalIntegrationIdProvider =
  (typeof CreateExternalIntegrationIdProvider)[keyof typeof CreateExternalIntegrationIdProvider];

export enum CreateExternalIntegrationIdType {
  RMA = "RMA",
  SUPPORT_CASE = "SUPPORT_CASE",
  CASH_REFUND = "CASH_REFUND",
  CREDIT_MEMO = "CREDIT_MEMO",
  REFUND = "REFUND",
  SALES_CREDIT = "SALES_CREDIT",
  RECEIPT = "RECEIPT",
  SALES_ORDER = "SALES_ORDER",
  VENDOR_RETURN_AUTHORIZATION = "VENDOR_RETURN_AUTHORIZATION",
  BOX = "BOX",
}

const createProviderAllowedTypes = {
  [CreateExternalIntegrationIdProvider.AIMS360]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.APPAREL_MAGIC]: [
    CreateExternalIntegrationIdType.RMA,
    CreateExternalIntegrationIdType.CREDIT_MEMO,
  ],
  [CreateExternalIntegrationIdProvider.BLUE_CHERRY]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.BRIGHTPEARL]: [
    CreateExternalIntegrationIdType.SALES_CREDIT,
  ],
  [CreateExternalIntegrationIdProvider.CIN7_OMNI]: [
    CreateExternalIntegrationIdType.SALES_CREDIT,
  ],
  [CreateExternalIntegrationIdProvider.CLOUDX]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.DEPOSCO]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.ECWID]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.EXTENSIV]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.FLEXPORT]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.GLOBALE]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.INMAR]: [
    CreateExternalIntegrationIdType.BOX,
  ],
  [CreateExternalIntegrationIdProvider.MASONHUB]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.MINTSOFT]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.NETSUITE]: [
    CreateExternalIntegrationIdType.RMA,
    CreateExternalIntegrationIdType.SUPPORT_CASE,
    CreateExternalIntegrationIdType.RECEIPT,
    CreateExternalIntegrationIdType.CASH_REFUND,
    CreateExternalIntegrationIdType.CREDIT_MEMO,
    CreateExternalIntegrationIdType.REFUND,
    CreateExternalIntegrationIdType.SALES_ORDER,
    CreateExternalIntegrationIdType.VENDOR_RETURN_AUTHORIZATION,
  ],
  [CreateExternalIntegrationIdProvider.RETURNBEAR]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.RETURNLOGIC]: [
    CreateExternalIntegrationIdType.RMA,
    CreateExternalIntegrationIdType.SALES_ORDER,
  ],
  [CreateExternalIntegrationIdProvider.RYDER]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SFOM]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SHIPBOB]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SHIPEDGE]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SHIPHERO]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SHIPMONK]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.TRACKSTAR]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.BLUEYONDER]: [
    CreateExternalIntegrationIdType.RMA,
  ],
} as const satisfies Record<
  CreateExternalIntegrationIdProvider,
  readonly CreateExternalIntegrationIdType[]
>;

type CreateAllowedTypesFor<P extends CreateExternalIntegrationIdProvider> =
  (typeof createProviderAllowedTypes)[P][number];

export type ValidCreateProviderTypePair = {
  [P in CreateExternalIntegrationIdProvider]: {
    provider: P;
    type: CreateAllowedTypesFor<P>;
  };
}[CreateExternalIntegrationIdProvider];

export type ValidCreateExternalIntegrationId = ValidCreateProviderTypePair & {
  id: string;
  shipmentGroupId?: string | null;
};

export type CreateExternalIntegrationId = NonNullable<
  ReturnZod["createExternalIntegrationIds"]
>[number];

export type FindCreateExternalIntegrationIdParams =
  ValidCreateProviderTypePair & { shipmentGroupId?: string; id?: string };

export function createExternalIntegrationId(
  params: ValidCreateExternalIntegrationId,
): CreateExternalIntegrationId {
  return params;
}

function matchesCreateExternalIntegrationIdParams(
  extId: CreateExternalIntegrationId,
  params: FindCreateExternalIntegrationIdParams,
): boolean {
  if (extId.provider !== params.provider) {
    return false;
  }
  if (extId.type !== params.type) {
    return false;
  }
  if (
    params.shipmentGroupId !== undefined &&
    extId.shipmentGroupId !== params.shipmentGroupId
  ) {
    return false;
  }
  if (params.id !== undefined && extId.id !== params.id) {
    return false;
  }
  return true;
}

export function findCreateExternalIntegrationId(
  createExternalIntegrationIds:
    | CreateExternalIntegrationId[]
    | undefined
    | null,
  params: FindCreateExternalIntegrationIdParams,
): CreateExternalIntegrationId | undefined {
  if (!createExternalIntegrationIds) {
    return undefined;
  }
  return createExternalIntegrationIds.find((extId) =>
    matchesCreateExternalIntegrationIdParams(extId, params),
  );
}

export function filterCreateExternalIntegrationIds(
  createExternalIntegrationIds:
    | CreateExternalIntegrationId[]
    | undefined
    | null,
  params: FindCreateExternalIntegrationIdParams,
): CreateExternalIntegrationId[] {
  if (!createExternalIntegrationIds) {
    return [];
  }
  return createExternalIntegrationIds.filter((extId) =>
    matchesCreateExternalIntegrationIdParams(extId, params),
  );
}

export function hasCreateExternalIntegrationId(
  createExternalIntegrationIds:
    | CreateExternalIntegrationId[]
    | undefined
    | null,
  params: FindCreateExternalIntegrationIdParams,
): boolean {
  return (
    findCreateExternalIntegrationId(createExternalIntegrationIds, params) !==
    undefined
  );
}

const integrationProviderToExternalRma: Record<
  IntegrationProviders,
  ExternalRmaIntegration
> = {
  [IntegrationProviders.AIMS360]: ExternalRmaIntegration.AIMS360,
  [IntegrationProviders.APPAREL_MAGIC]: ExternalRmaIntegration.APPAREL_MAGIC,
  [IntegrationProviders.BLUE_CHERRY]: ExternalRmaIntegration.BLUE_CHERRY,
  [IntegrationProviders.BRIGHTPEARL]: ExternalRmaIntegration.BRIGHTPEARL,
  [IntegrationProviders.CIN7_OMNI]: ExternalRmaIntegration.CIN7_OMNI,
  [IntegrationProviders.CLOUDX]: ExternalRmaIntegration.CLOUDX,
  [IntegrationProviders.DEPOSCO]: ExternalRmaIntegration.DEPOSCO,
  [IntegrationProviders.ECWID]: ExternalRmaIntegration.ECWID,
  [IntegrationProviders.EXTENSIV]: ExternalRmaIntegration.EXTENSIV,
  [IntegrationProviders.FLEXPORT]: ExternalRmaIntegration.FLEXPORT,
  [IntegrationProviders.GLOBALE]: ExternalRmaIntegration.GLOBALE,
  [IntegrationProviders.INMAR]: ExternalRmaIntegration.INMAR,
  [IntegrationProviders.MASONHUB]: ExternalRmaIntegration.MASONHUB,
  [IntegrationProviders.MINTSOFT]: ExternalRmaIntegration.MINTSOFT,
  [IntegrationProviders.NETSUITE]: ExternalRmaIntegration.NETSUITE,
  [IntegrationProviders.RYDER]: ExternalRmaIntegration.RYDER,
  [IntegrationProviders.SFOM]: ExternalRmaIntegration.SFOM,
  [IntegrationProviders.SHIPBOB]: ExternalRmaIntegration.SHIPBOB,
  [IntegrationProviders.SHIPEDGE]: ExternalRmaIntegration.SHIPEDGE,
  [IntegrationProviders.SHIPHERO]: ExternalRmaIntegration.SHIPHERO,
  [IntegrationProviders.SHIPMONK]: ExternalRmaIntegration.SHIPMONK,
  [IntegrationProviders.TRACKSTAR]: ExternalRmaIntegration.TRACKSTAR,
};

/**
 * Map a createRmaResults `name` onto the ExternalRmaIntegration enum used by
 * createExternalRmaResults.provider. Accepts three input shapes:
 *   - lowercase legacy IntegrationProviders values stored in old DB records
 *   - new ExternalRmaIntegration values written by current code
 *   - TrackstarReturnProvider sub-providers, which all collapse to TRACKSTAR
 *
 * IntegrationProviders and ExternalRmaIntegration are checked before
 * TrackstarReturnProvider because "shipmonk" and "deposco" appear in
 * IntegrationProviders, and ExternalRmaIntegration string values are distinct
 * from any TrackstarReturnProvider value.
 */
export function legacyRmaResultNameToExternalRmaIntegration(
  name: IntegrationProviders | TrackstarReturnProvider | ExternalRmaIntegration,
): ExternalRmaIntegration {
  const direct = integrationProviderToExternalRma[name as IntegrationProviders];
  if (direct !== undefined) {
    return direct;
  }
  if (
    Object.values(ExternalRmaIntegration).includes(
      name as ExternalRmaIntegration,
    )
  ) {
    return name as ExternalRmaIntegration;
  }
  return ExternalRmaIntegration.TRACKSTAR;
}

export function isRedoReturnsCenterDestination(return_: {
  merchant_address?: { returnsCenterId?: string | null } | null;
}): boolean {
  return !!return_.merchant_address?.returnsCenterId;
}

// Translates integrationName to its legacy lowercase DB form before comparing so PascalCase ExternalRmaIntegration values match createRmaResults[].name.
export function shouldSkipRmaResultForIntegration(
  createRmaResults:
    | readonly {
        name: IntegrationProviders | TrackstarReturnProvider;
        merchantStopRetries: boolean;
      }[]
    | null
    | undefined,
  integrationName: ExternalRmaIntegration | TrackstarReturnProvider,
): boolean {
  const legacyName =
    externalRmaIntegrationToLegacyRmaResultName(integrationName);
  return (
    createRmaResults?.some(
      (r) => r.name === legacyName && r.merchantStopRetries,
    ) ?? false
  );
}

/**
 * Reverse of integrationProviderToExternalRma. Used at the legacy
 * createRmaResults[].name write boundary so that runtime values stay
 * lowercase even though ReturnIntegration subclasses now construct with
 * ExternalRmaIntegration values. Remove together with the legacy
 * createRmaResults field.
 */
const externalRmaIntegrationToLegacyProvider = {
  [ExternalRmaIntegration.AIMS360]: IntegrationProviders.AIMS360,
  [ExternalRmaIntegration.APPAREL_MAGIC]: IntegrationProviders.APPAREL_MAGIC,
  [ExternalRmaIntegration.BLUE_CHERRY]: IntegrationProviders.BLUE_CHERRY,
  [ExternalRmaIntegration.BRIGHTPEARL]: IntegrationProviders.BRIGHTPEARL,
  [ExternalRmaIntegration.CIN7_OMNI]: IntegrationProviders.CIN7_OMNI,
  [ExternalRmaIntegration.CLOUDX]: IntegrationProviders.CLOUDX,
  [ExternalRmaIntegration.DEPOSCO]: IntegrationProviders.DEPOSCO,
  [ExternalRmaIntegration.ECWID]: IntegrationProviders.ECWID,
  [ExternalRmaIntegration.EXTENSIV]: IntegrationProviders.EXTENSIV,
  [ExternalRmaIntegration.FLEXPORT]: IntegrationProviders.FLEXPORT,
  [ExternalRmaIntegration.GLOBALE]: IntegrationProviders.GLOBALE,
  [ExternalRmaIntegration.INMAR]: IntegrationProviders.INMAR,
  [ExternalRmaIntegration.MASONHUB]: IntegrationProviders.MASONHUB,
  [ExternalRmaIntegration.MINTSOFT]: IntegrationProviders.MINTSOFT,
  [ExternalRmaIntegration.NETSUITE]: IntegrationProviders.NETSUITE,
  [ExternalRmaIntegration.RYDER]: IntegrationProviders.RYDER,
  [ExternalRmaIntegration.SFOM]: IntegrationProviders.SFOM,
  [ExternalRmaIntegration.SHIPBOB]: IntegrationProviders.SHIPBOB,
  [ExternalRmaIntegration.SHIPEDGE]: IntegrationProviders.SHIPEDGE,
  [ExternalRmaIntegration.SHIPHERO]: IntegrationProviders.SHIPHERO,
  [ExternalRmaIntegration.SHIPMONK]: IntegrationProviders.SHIPMONK,
  [ExternalRmaIntegration.TRACKSTAR]: IntegrationProviders.TRACKSTAR,
} as const satisfies Record<ExternalRmaIntegration, IntegrationProviders>;

/**
 * Convert an ExternalRmaIntegration or TrackstarReturnProvider value into the
 * lowercase IntegrationProviders | TrackstarReturnProvider form that the
 * legacy createRmaResults[].name field stores. TrackstarReturnProvider values
 * pass through unchanged (they're already lowercase and not in
 * IntegrationProviders). Used only by the legacy field writer.
 */
export function externalRmaIntegrationToLegacyRmaResultName(
  name: ExternalRmaIntegration | TrackstarReturnProvider,
): IntegrationProviders | TrackstarReturnProvider {
  return (
    externalRmaIntegrationToLegacyProvider[name as ExternalRmaIntegration] ??
    (name as TrackstarReturnProvider)
  );
}

/** Carrier name reported for each dropoff provider's tracking identifier. */
const BLUE_YONDER_CARRIER = "FedEx";
const VIRTUAL_CARRIER = "UniUni";
const RETURNBEAR_CARRIER = "Staples";

export interface ReturnCarrierAndTracking {
  carrier: string;
  trackingCode: string;
}

// Structural rather than `Pick<ReturnZod, "dropoffs" | "shipment" | "shipments">`:
// callers pass the mongoose `ReturnRaw`, which is NOT assignable to `ReturnZod`
// (e.g. `ReturnRaw.shipment` is `ShipmentRaw | null | undefined`, while
// `ReturnZod.shipment` is `Shipment | undefined` with no `null`, and the
// `ShipmentRaw`/zod `Shipment` shapes diverge). This mirrors the structural
// input convention used by `getReturner`.
interface ReturnShippingSource {
  dropoffs?: {
    chosen?: boolean | null;
    qrCodes:
      | {
          provider?: DropoffProviderType | string | null;
          shipmentGroupId?: string | null;
          trackingNumber?: string | null;
          providerMetadata?: unknown;
        }[]
      | null;
  } | null;
  shipment?: ReturnShippingShipment | null;
  shipments?: ReturnShippingShipment[] | null;
}

interface ReturnShippingShipment {
  shipmentGroupID?: string | null;
  _shipment?: {
    tracking_code?: string | null;
    tracker?: { tracking_code?: string | null } | null;
    selected_rate?: { carrier?: string | null } | null;
  } | null;
}

/**
 * Resolves the carrier and tracking code for a return, preferring a chosen
 * dropoff over the return's own shipment.
 *
 * - Dropoff: a BlueYonder RMA id (FedEx Easy Returns) is used first, then the
 *   chosen dropoff provider's identifier (BlueYonder -> FedEx, Virtual ->
 *   UniUni, ReturnBear -> Staples).
 * - Shipment: when `shipmentGroupId` is given the matching shipment is used,
 *   otherwise the first shipment (falling back to the singular `shipment`).
 *
 * Returns `undefined` unless both a carrier and a (non-empty) tracking code can
 * be resolved — a tracking code with no carrier is not usable.
 */
export function getReturnCarrierAndTracking(
  return_: ReturnShippingSource,
  shipmentGroupId?: string,
): ReturnCarrierAndTracking | undefined {
  return (
    getDropoffCarrierAndTracking(return_, shipmentGroupId) ??
    getShipmentCarrierAndTracking(return_, shipmentGroupId)
  );
}

function getDropoffCarrierAndTracking(
  return_: ReturnShippingSource,
  shipmentGroupId?: string,
): ReturnCarrierAndTracking | undefined {
  if (!isDropoffChosen(return_)) {
    return undefined;
  }

  const provider = getChosenDropoffProvider(return_, shipmentGroupId);
  if (!provider) {
    return undefined;
  }

  const trackingCode = getDropoffIdentifierForProvider(
    return_,
    provider,
    shipmentGroupId,
  );
  if (!trackingCode) {
    return undefined;
  }

  switch (provider) {
    case BLUE_YONDER_PROVIDER:
      return { carrier: BLUE_YONDER_CARRIER, trackingCode };
    case VIRTUAL_PROVIDER:
      return { carrier: VIRTUAL_CARRIER, trackingCode };
    default:
      return { carrier: RETURNBEAR_CARRIER, trackingCode };
  }
}

function getChosenDropoffProvider(
  return_: ReturnShippingSource,
  shipmentGroupId?: string,
): DropoffProviderType | undefined {
  const entry = (return_.dropoffs?.qrCodes ?? []).find(
    (qr) => !shipmentGroupId || qr.shipmentGroupId === shipmentGroupId,
  );
  switch (entry?.provider) {
    case BLUE_YONDER_PROVIDER:
    case VIRTUAL_PROVIDER:
    case RETURN_BEAR_PROVIDER:
      return entry.provider;
    default:
      return undefined;
  }
}

function getShipmentCarrierAndTracking(
  return_: ReturnShippingSource,
  shipmentGroupId?: string,
): ReturnCarrierAndTracking | undefined {
  const shipment = pickShipment(return_, shipmentGroupId)?._shipment;

  const trackingCode = (
    shipment?.tracker?.tracking_code ?? shipment?.tracking_code
  )?.trim();
  const carrier = shipment?.selected_rate?.carrier?.trim();

  // Require both — a tracking code with no carrier isn't usable.
  if (!trackingCode || !carrier) {
    return undefined;
  }

  return { carrier, trackingCode };
}

function pickShipment(
  return_: ReturnShippingSource,
  shipmentGroupId?: string,
): ReturnShippingShipment | undefined {
  // When a group is explicitly requested, only that group's shipment is
  // correct — never fall back to another group's shipment, or we'd attach the
  // wrong carrier/tracking to this RMA. The bare fallback is for groupless
  // callers (e.g. Ryder, ShipBob).
  if (shipmentGroupId) {
    return return_.shipments?.find(
      (shipment) => shipment.shipmentGroupID === shipmentGroupId,
    );
  }
  return return_.shipments?.[0] ?? return_.shipment ?? undefined;
}
